import dayjs from "dayjs";
import type { TBirthdayMonth, TCatFormDataInput } from "@./suf-store";
import { CAT_VALIDATION } from "@./cat-calculations";
import { NUMBER_OF_MONTHS_IN_A_YEAR } from "libs/utils/misc/src/dates";

import { SENIOR_AGE_THRESHOLD_YEARS } from "../constants";

type TCatAgeUpdate = Pick<TCatFormDataInput, "lifeStage" | "age" | "birthdayMonth">;

const getKittenAgeUpdate = (
  cat: TCatFormDataInput,
  birthday: dayjs.Dayjs,
): TCatAgeUpdate => {
  const totalMonths = dayjs().diff(birthday, "month");

  if (totalMonths >= NUMBER_OF_MONTHS_IN_A_YEAR) {
    return {
      lifeStage: "adult",
      age: { mode: "years", years: Math.floor(totalMonths / NUMBER_OF_MONTHS_IN_A_YEAR) },
      birthdayMonth: birthday.format("MMMM").toUpperCase() as TBirthdayMonth,
    };
  }

  const totalWeeks = dayjs().diff(birthday, "week");
  if (cat.age?.mode === "weeks" && totalWeeks <= CAT_VALIDATION.MAX_AGE_WEEKS) {
    return { age: { ...cat.age, weeks: totalWeeks } };
  }

  return { age: { ...cat.age, mode: "months", months: totalMonths } };
};

const getGrownCatAgeUpdate = (
  cat: TCatFormDataInput,
  birthday: dayjs.Dayjs,
): TCatAgeUpdate => {
  const years = dayjs().diff(birthday, "year");
  const lifeStage = years > SENIOR_AGE_THRESHOLD_YEARS ? "senior" : "adult";
  return { lifeStage, age: { ...cat.age, years } };
};

const isUnchanged = (cat: TCatFormDataInput, update: TCatAgeUpdate): boolean =>
  (update.lifeStage ?? cat.lifeStage) === cat.lifeStage &&
  (update.birthdayMonth ?? cat.birthdayMonth) === cat.birthdayMonth &&
  update.age?.mode === cat.age?.mode &&
  update.age?.years === cat.age?.years &&
  update.age?.months === cat.age?.months &&
  update.age?.weeks === cat.age?.weeks;

const refreshCatAge = <T extends TCatFormDataInput>(cat: T): T => {
  const birthday = cat.birthday ? dayjs(cat.birthday) : undefined;
  if (!birthday?.isValid() || !cat.lifeStage) {
    return cat;
  }

  const update = cat.lifeStage === "kitten"
    ? getKittenAgeUpdate(cat, birthday)
    : getGrownCatAgeUpdate(cat, birthday);

  return isUnchanged(cat, update) ? cat : { ...cat, ...update };
};

/**
 * Brings each rehydrated cat's age up to date using its stored birthday, so a
 * returning user's cat isn't still the age it was when they left the flow.
 *
 * Life stage follows the new age: kittens become adults at 12 months and
 * adults become seniors after 8 years (matching `useLifeStageLogic`).
 * Kittens entered in weeks stay in weeks until they pass the weeks limit.
 * Cats without a birthday (the user never got past the Age page) are left alone.
 */
export function refreshCatAges<T extends TCatFormDataInput>(
  cats: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(cats).map(([ id, cat ]) => [ id, refreshCatAge(cat) ]),
  );
}
